import React, { useState, useCallback, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Loader2,
  Plus,
  ArrowDownCircle,
  ArrowUpCircle,
  MinusCircle,
  ExternalLink,
  Package,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { usePermissions } from '@hooks/usePermissions';
import stockOverviewService, {
  StockOverviewProduct,
  ProviderWithProducts,
} from '../../services/stockOverviewService';
import { stockMovementService } from '../../services/stockMovementService';
import { productService } from '../../services/productService';
import {
  StockMovement,
  MovementType,
  OriginType,
  MANUAL_ORIGIN_TYPE_OPTIONS,
  MOVEMENT_TYPE_BY_ORIGIN,
  StockMovementFormData,
} from '../../types/stockMovement';
import { clsx } from 'clsx';

// ─── Helpers ──────────────────────────────────────────────────────

const formatQty = (value?: number | null) => {
  if (value === undefined || value === null) return '—';
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatMovDate = (dateStr: string) =>
  new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

// ─── Badges ──────────────────────────────────────────────────────

const MovementTypeBadge: React.FC<{ type: number }> = ({ type }) => {
  const config: Record<number, { label: string; className: string; icon: React.ReactNode }> = {
    [MovementType.Entrada]: { label: 'Entrada', className: 'bg-green-100 text-green-700 border-green-200', icon: <ArrowDownCircle size={14} /> },
    [MovementType.Saida]: { label: 'Saída', className: 'bg-red-100 text-red-700 border-red-200', icon: <ArrowUpCircle size={14} /> },
    [MovementType.Saldo]: { label: 'Saldo', className: 'bg-blue-100 text-blue-700 border-blue-200', icon: <MinusCircle size={14} /> },
  };
  const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600', icon: null };
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
      {c.icon} {c.label}
    </span>
  );
};

const OriginTypeBadge: React.FC<{ type: number }> = ({ type }) => {
  const config: Record<number, { label: string; className: string }> = {
    [OriginType.PedidoCompra]: { label: 'Pedido de Compra', className: 'bg-purple-100 text-purple-700 border-purple-200' },
    [OriginType.PedidoVenda]: { label: 'Pedido de Venda', className: 'bg-orange-100 text-orange-700 border-orange-200' },
    [OriginType.AcertoManual]: { label: 'Acerto Manual', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    [OriginType.Balanco]: { label: 'Balanço', className: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
    [OriginType.RecebimentoMaterial]: { label: 'Recebimento Material', className: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  };
  const c = config[type] || { label: 'Desconhecido', className: 'bg-slate-100 text-slate-600' };
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border', c.className)}>
      {c.label}
    </span>
  );
};

// ─── Stock Movement Form (inline) ────────────────────────────────

interface StockMovementFormProps {
  productId: number;
  onCreated: (mov: StockMovement) => void;
  onCancel: () => void;
}

const InlineStockMovementForm: React.FC<StockMovementFormProps> = ({ productId, onCreated, onCancel }) => {
  const [originType, setOriginType] = useState<number>(OriginType.AcertoManual);
  const [movementType, setMovementType] = useState<number>(MovementType.Entrada);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [costPrice, setCostPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const movementTypeOptions = MOVEMENT_TYPE_BY_ORIGIN[originType] || [];

  useEffect(() => {
    if (movementTypeOptions.length > 0) {
      setMovementType(movementTypeOptions[0].value);
    }
  }, [originType]);

  const handleSubmit = async () => {
    if (!quantity || parseFloat(quantity) <= 0) {
      setError('Quantidade é obrigatória e deve ser maior que zero');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const data: StockMovementFormData = {
        origin_type: originType,
        movement_type: movementType,
        quantity: parseFloat(quantity),
        notes: notes || undefined,
        product_id: productId,
        purchase_price: purchasePrice ? parseFloat(purchasePrice) : undefined,
        cost_price: costPrice ? parseFloat(costPrice) : undefined,
      };
      const mov = await stockMovementService.createMovement(data);
      onCreated(mov);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar lançamento');
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className="bg-amber-50 border-b border-amber-200">
      <td colSpan={11} className="px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Origem</label>
            <select
              value={originType}
              onChange={(e) => setOriginType(Number(e.target.value))}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {MANUAL_ORIGIN_TYPE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
            <select
              value={movementType}
              onChange={(e) => setMovementType(Number(e.target.value))}
              className="border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            >
              {movementTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Quantidade *</label>
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-24 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Preço Compra</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={purchasePrice}
              onChange={(e) => setPurchasePrice(e.target.value)}
              className="w-24 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="0,00"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Preço Custo</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={costPrice}
              onChange={(e) => setCostPrice(e.target.value)}
              className="w-24 border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="0,00"
            />
          </div>
          <div className="flex-1 min-w-[120px]">
            <label className="block text-xs font-medium text-slate-600 mb-1">Observação</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
              placeholder="Opcional"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
            >
              {saving ? <RefreshCw size={12} className="animate-spin" /> : <Plus size={12} />}
              Salvar
            </button>
            <button
              onClick={onCancel}
              disabled={saving}
              className="px-3 py-1.5 border border-slate-300 text-slate-600 text-xs rounded hover:bg-slate-50 disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
        {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
      </td>
    </tr>
  );
};

// ─── Recalculate Progress Modal ──────────────────────────────────

interface RecalculateModalProps {
  providerName: string;
  products: StockOverviewProduct[];
  onClose: () => void;
  onComplete: (updatedProducts: Map<number, { stock_quantity: number; stock_quantity_available: number }>) => void;
}

const RecalculateProgressModal: React.FC<RecalculateModalProps> = ({ providerName, products, onClose, onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [total] = useState(products.length);
  const [running, setRunning] = useState(true);
  const [errors, setErrors] = useState<string[]>([]);
  const [updatedMap] = useState(new Map<number, { stock_quantity: number; stock_quantity_available: number }>());

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      for (let i = 0; i < products.length; i++) {
        if (cancelled) break;
        try {
          const result = await productService.recalculateStock(products[i].id);
          updatedMap.set(products[i].id, {
            stock_quantity: result.stock_quantity,
            stock_quantity_available: result.new_available,
          });
        } catch (err) {
          setErrors(prev => [...prev, `${products[i].sku}: ${err instanceof Error ? err.message : 'Erro'}`]);
        }
        setCurrent(i + 1);
      }
      setRunning(false);
      onComplete(updatedMap);
    };

    run();
    return () => { cancelled = true; };
  }, []);

  const progress = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">
        <h2 className="text-lg font-semibold text-slate-900">Recalculando Estoque Disponível</h2>
        <p className="text-sm text-slate-500">
          Fornecedor: <span className="font-medium text-slate-700">{providerName}</span>
        </p>

        <div className="w-full bg-slate-200 rounded-full h-3">
          <div
            className="bg-emerald-500 h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-sm text-slate-600 text-center">
          {current} / {total} produtos processados ({progress}%)
        </p>

        {errors.length > 0 && (
          <div className="max-h-24 overflow-y-auto bg-red-50 border border-red-200 rounded-lg p-2">
            {errors.map((e, i) => (
              <p key={i} className="text-xs text-red-600">{e}</p>
            ))}
          </div>
        )}

        {!running && (
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────

export const StockOverviewReportView: React.FC = () => {
  const navigate = useNavigate();
  const { checkPermission } = usePermissions();
  const canCreateMovement = checkPermission('stock_movements.create').hasPermission;
  const canRecalculate = checkPermission('products.recalculate_stock').hasPermission;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerData, setProviderData] = useState<ProviderWithProducts[]>([]);
  const [activeProviderTab, setActiveProviderTab] = useState<number>(0);

  // Expanded products state: productId -> movements data
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
  const [productMovements, setProductMovements] = useState<Map<number, StockMovement[]>>(new Map());
  const [productMovementsLoading, setProductMovementsLoading] = useState<Set<number>>(new Set());

  // Inline form state
  const [addMovementForProduct, setAddMovementForProduct] = useState<number | null>(null);

  // Recalculate modal state
  const [recalculateModal, setRecalculateModal] = useState<{
    providerName: string;
    products: StockOverviewProduct[];
  } | null>(null);

  // Recalculating single product
  const [recalculatingProduct, setRecalculatingProduct] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await stockOverviewService.getProvidersWithProducts();
      // Sort products: with stock first, then without stock
      const sortedData = data.map(item => ({
        ...item,
        products: [...item.products].sort((a, b) => {
          const aHasStock = (a.stock_quantity ?? 0) > 0 ? 0 : 1;
          const bHasStock = (b.stock_quantity ?? 0) > 0 ? 0 : 1;
          if (aHasStock !== bHasStock) return aHasStock - bHasStock;
          return (a.sku || '').localeCompare(b.sku || '');
        }),
      }));
      setProviderData(sortedData);
      if (sortedData.length > 0) setActiveProviderTab(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleProduct = useCallback(async (productId: number) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
        return next;
      }
      next.add(productId);
      return next;
    });

    // Load movements if not yet loaded
    if (!productMovements.has(productId)) {
      setProductMovementsLoading(prev => new Set(prev).add(productId));
      try {
        const result = await stockOverviewService.getMovementsByProduct(productId, {
          page: 1,
          pageSize: 50,
        });
        setProductMovements(prev => new Map(prev).set(productId, result.data));
      } catch {
        // silently fail
      } finally {
        setProductMovementsLoading(prev => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    }
  }, [productMovements]);

  const handleMovementCreated = (productId: number, mov: StockMovement) => {
    // Add movement to the list
    setProductMovements(prev => {
      const current = prev.get(productId) || [];
      return new Map(prev).set(productId, [mov, ...current]);
    });
    // Update product stock values in the provider data
    setProviderData(prev => prev.map(prov => ({
      ...prov,
      products: prov.products.map(p =>
        p.id === productId
          ? {
              ...p,
              stock_quantity: mov.new_balance ?? p.stock_quantity,
              stock_quantity_available: mov.movement_type === MovementType.Saldo
                ? (mov.new_balance ?? p.stock_quantity_available)
                : p.stock_quantity_available,
            }
          : p,
      ),
    })));
    setAddMovementForProduct(null);
  };

  const handleRecalculateProduct = async (productId: number) => {
    setRecalculatingProduct(productId);
    try {
      const result = await productService.recalculateStock(productId);
      setProviderData(prev => prev.map(prov => ({
        ...prov,
        products: prov.products.map(p =>
          p.id === productId
            ? {
                ...p,
                stock_quantity: result.stock_quantity,
                stock_quantity_available: result.new_available,
              }
            : p,
        ),
      })));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao recalcular estoque');
    } finally {
      setRecalculatingProduct(null);
    }
  };

  const handleRecalculateProvider = (providerIndex: number) => {
    const prov = providerData[providerIndex];
    if (!prov) return;
    setRecalculateModal({
      providerName: prov.provider.trade_name || prov.provider.legal_name,
      products: prov.products,
    });
  };

  const handleRecalculateComplete = (updatedMap: Map<number, { stock_quantity: number; stock_quantity_available: number }>) => {
    setProviderData(prev => prev.map(prov => ({
      ...prov,
      products: prov.products.map(p => {
        const updated = updatedMap.get(p.id);
        return updated ? { ...p, ...updated } : p;
      }),
    })));
  };

  const renderOriginRef = (mov: StockMovement) => {
    if (mov.origin_type === OriginType.PedidoVenda && mov.order_id) {
      return (
        <button
          className="text-orange-600 hover:text-orange-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => navigate(`/sales-orders/${mov.order_id}`)}
        >
          <ExternalLink size={12} /> Pedido #{mov.order_id}
        </button>
      );
    }
    if (mov.origin_type === OriginType.RecebimentoMaterial && mov.material_receipt_id) {
      return (
        <button
          className="text-indigo-600 hover:text-indigo-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => navigate(`/material-receipts/${mov.material_receipt_id}`)}
        >
          <ExternalLink size={12} /> Receb. #{mov.material_receipt_id}
        </button>
      );
    }
    if ((mov.origin_type === OriginType.PedidoCompra || mov.origin_type === OriginType.RecebimentoMaterial) && mov.purchase_order_id) {
      return (
        <button
          className="text-purple-600 hover:text-purple-800 hover:underline text-xs font-medium inline-flex items-center gap-1"
          onClick={() => navigate(`/purchase-orders/${mov.purchase_order_id}`)}
        >
          <ExternalLink size={12} /> PC #{mov.purchase_order_number || mov.purchase_order_id}
        </button>
      );
    }
    if (mov.origin_reference) {
      return <span className="text-xs text-slate-500">{mov.origin_reference}</span>;
    }
    return <span className="text-xs text-slate-400">—</span>;
  };

  const activeProvider = providerData[activeProviderTab];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">Visão Geral do Estoque</h2>
        <p className="text-sm text-slate-500 mt-1">
          Produtos agrupados por fornecedor com movimentações de estoque
        </p>
      </div>

      {/* Load button */}
      {!loading && providerData.length === 0 && !error && (
        <Card>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-8">
              <Package size={40} className="text-slate-300 mb-3" />
              <p className="text-slate-500 mb-4">Clique para carregar a visão geral do estoque</p>
              <Button variant="primary" onClick={loadData}>
                Carregar Relatório
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-emerald-600" />
        </div>
      )}

      {providerData.length > 0 && !loading && (
        <>
          {/* Provider tabs */}
          <div className="border-b border-slate-200 overflow-x-auto">
            <nav className="flex -mb-px gap-0" aria-label="Fornecedores">
              {providerData.map((item, index) => {
                const name = item.provider.trade_name || item.provider.legal_name;
                const isActive = index === activeProviderTab;
                return (
                  <button
                    key={item.provider.provider_id}
                    onClick={() => setActiveProviderTab(index)}
                    className={clsx(
                      'whitespace-nowrap py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                      isActive
                        ? 'border-emerald-500 text-emerald-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300',
                    )}
                  >
                    {name}
                    <span className="ml-1.5 text-xs text-slate-400">({item.products.length})</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Active provider content */}
          {activeProvider && (
            <Card>
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {activeProvider.provider.trade_name || activeProvider.provider.legal_name}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {activeProvider.provider.document} — {activeProvider.products.length} produto(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<RefreshCw size={14} />}
                    onClick={loadData}
                  >
                    Atualizar
                  </Button>
                  {canRecalculate && (
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<RefreshCw size={14} />}
                      onClick={() => handleRecalculateProvider(activeProviderTab)}
                    >
                      Recalcular estoque disponível
                    </Button>
                  )}
                </div>
              </div>

              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-3 py-2 text-left w-10" />
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">SKU</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-slate-500 uppercase">Descrição</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Est. Físico</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-slate-500 uppercase">Est. Disponível</th>
                        {(canCreateMovement || canRecalculate) && (
                          <th className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase">Ações</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {activeProvider.products.map(product => {
                        const isExpanded = expandedProducts.has(product.id);
                        const movements = productMovements.get(product.id) || [];
                        const isLoadingMovements = productMovementsLoading.has(product.id);
                        const hasStock = (product.stock_quantity ?? 0) > 0;

                        return (
                          <React.Fragment key={product.id}>
                            {/* Product row */}
                            <tr className={clsx(
                              'border-b border-slate-100 transition-colors',
                              isExpanded ? 'bg-emerald-50' : hasStock ? 'bg-white hover:bg-slate-50' : 'bg-slate-50 hover:bg-slate-100',
                            )}>
                              <td className="px-3 py-2">
                                <button
                                  onClick={() => toggleProduct(product.id)}
                                  className="p-1 rounded hover:bg-slate-200 transition-colors text-slate-500"
                                >
                                  {isLoadingMovements ? (
                                    <RefreshCw size={14} className="animate-spin" />
                                  ) : isExpanded ? (
                                    <ChevronDown size={14} />
                                  ) : (
                                    <ChevronRight size={14} />
                                  )}
                                </button>
                              </td>
                              <td className="px-3 py-2">
                                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs font-mono">{product.sku}</code>
                              </td>
                              <td className="px-3 py-2 text-slate-700 max-w-xs truncate" title={product.description || ''}>
                                {product.description || '—'}
                              </td>
                              <td className="px-3 py-2 text-right font-medium">
                                {formatQty(product.stock_quantity)}
                              </td>
                              <td className={clsx(
                                'px-3 py-2 text-right font-medium',
                                (product.stock_quantity_available ?? 0) < 0
                                  ? 'text-red-600'
                                  : (product.stock_quantity_available ?? 0) === 0
                                    ? 'text-amber-600'
                                    : 'text-emerald-600',
                              )}>
                                {formatQty(product.stock_quantity_available)}
                              </td>
                              {(canCreateMovement || canRecalculate) && (
                                <td className="px-3 py-2 text-center">
                                  <div className="flex items-center justify-center gap-1">
                                    {canCreateMovement && (
                                      <button
                                        onClick={() => setAddMovementForProduct(product.id)}
                                        className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded"
                                        title="Novo lançamento de estoque"
                                      >
                                        <Plus size={14} />
                                      </button>
                                    )}
                                    {canRecalculate && (
                                      <button
                                        onClick={() => handleRecalculateProduct(product.id)}
                                        disabled={recalculatingProduct === product.id}
                                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded disabled:opacity-50"
                                        title="Recalcular estoque disponível"
                                      >
                                        <RefreshCw size={14} className={recalculatingProduct === product.id ? 'animate-spin' : ''} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>

                            {/* Inline form for new movement */}
                            {addMovementForProduct === product.id && (
                              <InlineStockMovementForm
                                productId={product.id}
                                onCreated={(mov) => handleMovementCreated(product.id, mov)}
                                onCancel={() => setAddMovementForProduct(null)}
                              />
                            )}

                            {/* Expanded movements */}
                            {isExpanded && (
                              <>
                                {isLoadingMovements ? (
                                  <tr className="bg-slate-50">
                                    <td colSpan={6} className="text-center py-4">
                                      <Loader2 size={16} className="animate-spin mx-auto text-slate-400" />
                                    </td>
                                  </tr>
                                ) : movements.length === 0 ? (
                                  <tr className="bg-slate-50">
                                    <td colSpan={6} className="text-center py-4 text-xs text-slate-400">
                                      Nenhuma movimentação de estoque encontrada
                                    </td>
                                  </tr>
                                ) : (
                                  <>
                                    {/* Movements header */}
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                      <td className="w-10" />
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500">Data</th>
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500">Origem</th>
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500">Referência</th>
                                      <th className="px-3 py-1.5 text-center text-xs font-medium text-slate-500">Tipo</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-slate-500">Qtd</th>
                                    </tr>
                                    <tr className="bg-slate-100 border-b border-slate-200">
                                      <td className="w-10" />
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-slate-500">Saldo Ant.</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-slate-500">Novo Saldo</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-slate-500">Preço Compra</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-slate-500">Preço Custo</th>
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-slate-500">Obs / Usuário</th>
                                    </tr>
                                    {movements.map(mov => (
                                      <React.Fragment key={mov.id}>
                                        <tr className="bg-white border-b border-slate-50 text-xs">
                                          <td className="w-10" />
                                          <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">{formatMovDate(mov.movement_date)}</td>
                                          <td className="px-3 py-1.5"><OriginTypeBadge type={mov.origin_type} /></td>
                                          <td className="px-3 py-1.5">{renderOriginRef(mov)}</td>
                                          <td className="px-3 py-1.5 text-center"><MovementTypeBadge type={mov.movement_type} /></td>
                                          <td className="px-3 py-1.5 text-right font-medium">{formatQty(mov.quantity)}</td>
                                        </tr>
                                        <tr className="bg-white border-b border-slate-100 text-xs">
                                          <td className="w-10" />
                                          <td className="px-3 py-1 text-right text-slate-500">{formatQty(mov.previous_balance)}</td>
                                          <td className="px-3 py-1 text-right text-slate-500">{formatQty(mov.new_balance)}</td>
                                          <td className="px-3 py-1 text-right text-slate-500">{mov.purchase_price != null ? formatCurrency(mov.purchase_price) : '—'}</td>
                                          <td className="px-3 py-1 text-right text-slate-500">{mov.cost_price != null ? formatCurrency(mov.cost_price) : '—'}</td>
                                          <td className="px-3 py-1 text-slate-500 max-w-xs truncate">{mov.notes || '—'} <span className="text-slate-400">• {mov.created_by}</span></td>
                                        </tr>
                                      </React.Fragment>
                                    ))}
                                  </>
                                )}
                              </>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Recalculate progress modal */}
      {recalculateModal && (
        <RecalculateProgressModal
          providerName={recalculateModal.providerName}
          products={recalculateModal.products}
          onClose={() => setRecalculateModal(null)}
          onComplete={handleRecalculateComplete}
        />
      )}
    </div>
  );
};

export default StockOverviewReportView;
